/**
 * Service pour gérer les forfaits
 */
import { API_BASE_URL } from '../api';
import { authService } from './authService';
import type { Package, PackagePurchaseRequest, PackagePurchase } from '../types/package';

class PackageService {
  private baseUrl = `${API_BASE_URL}/api/packages`;

  /**
   * Récupère la liste des forfaits disponibles
   */
  async getAvailablePackages(): Promise<Package[]> {
    const response = await fetch(this.baseUrl);
    if (!response.ok) {
      throw new Error('Erreur lors de la récupération des forfaits');
    }
    return response.json();
  }

  /**
   * Crée un achat de forfait (avant paiement)
   */
  async createPurchase(data: PackagePurchaseRequest): Promise<PackagePurchase> {
    const response = await fetch(`${this.baseUrl}/purchase`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Erreur inconnue' }));
      throw new Error(error.detail || 'Erreur lors de la création de l\'achat');
    }

    return response.json();
  }

  /**
   * Récupère les détails d'un achat de forfait
   */
  /**
   * Confirme le paiement d'un forfait juste après le paiement Stripe.
   * Le backend vérifie le paiement, génère les bons et envoie les emails,
   * sans dépendre du webhook Stripe. Idempotent.
   */
  async markPaid(purchaseId: number, stripePaymentIntentId: string): Promise<{
    success: boolean;
    purchase_id: number;
    payment_status: string;
    already_paid: boolean;
  }> {
    const url = new URL(`${this.baseUrl}/${purchaseId}/mark-paid`);
    url.searchParams.set('stripe_payment_intent_id', stripePaymentIntentId);
    const response = await fetch(url.toString(), { method: 'PUT' });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Erreur lors de la confirmation du paiement du forfait' }));
      throw new Error(error.detail || 'Erreur lors de la confirmation du paiement du forfait');
    }
    return response.json();
  }

  /**
   * Renvoie à l'acheteur l'email de confirmation avec les bons en PDF (backoffice)
   */
  async resendEmail(purchaseId: number): Promise<{ success: boolean; sent_to: string; attachments: number; message: string }> {
    const headers = await authService.getAuthHeaders();
    const response = await fetch(`${this.baseUrl}/${purchaseId}/resend-email`, {
      method: 'POST',
      headers,
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: "Erreur lors du renvoi de l'email" }));
      throw new Error(error.detail || "Erreur lors du renvoi de l'email");
    }
    return response.json();
  }

  async getPurchase(purchaseId: number): Promise<PackagePurchase> {
    const response = await fetch(`${this.baseUrl}/${purchaseId}`);
    if (!response.ok) {
      throw new Error('Erreur lors de la récupération de l\'achat');
    }
    return response.json();
  }

  /**
   * Liste tous les achats de forfaits (backoffice)
   */
  async listPurchases(params?: { status?: string; limit?: number; offset?: number }): Promise<PackagePurchase[]> {
    const url = new URL(`${this.baseUrl}/purchases/list`);
    if (params?.status) url.searchParams.set('status', params.status);
    if (params?.limit) url.searchParams.set('limit', params.limit.toString());
    if (params?.offset) url.searchParams.set('offset', params.offset.toString());

    const response = await fetch(url.toString());
    if (!response.ok) {
      throw new Error('Erreur lors de la récupération des achats de forfaits');
    }
    return response.json();
  }
}

export const packageService = new PackageService();
